import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../auth/AuthProvider';
import { Button, EmptyState, Input, Label, Select } from '../components/ui';
import { ColumnMenu, type SortDir } from '../components/ColumnMenu';
import { ServiceRouteView } from './ServiceRouteView';
import { AddServiceItemDialog } from './AddServiceItemDialog';
import { useBulkCompleteServiceItems, useBulkDeleteServiceItems } from '../lib/serviceMutations';
import {
    useServiceCommunities,
    useServiceCrews,
    useServiceItems,
    useServiceSections,
    useServiceStatusCounts,
} from '../lib/serviceQueries';
import {
    SERVICE_COMPLETED_STATUSES,
    SERVICE_PRIORITY_DEFAULT,
    SERVICE_PRIORITY_LABELS,
    SERVICE_PRIORITY_ORDER,
    SERVICE_STATE_LABELS,
    SERVICE_STATE_ORDER,
    SERVICE_STATUS_LABELS,
    SERVICE_STATUS_ORDER,
    communityLabel,
    crewLabel,
    scheduleLabel,
    type ServiceItemStatus,
    type ServiceItemWithContext,
} from '../lib/serviceTypes';
import {
    EditableFlag,
    EditableMoney,
    EditableDate,
    EditableSchedule,
    EditableSelect,
    EditableText,
    EditableTextarea,
} from './ServiceCells';
import { cn, formatDateShort } from '../lib/utils';
import { ColumnsPanel } from '../components/ColumnsPanel';
import { useSetUserPreferences, useUserPreferences } from '../lib/userPreferences';

/**
 * Two views, matching the schedule board.
 *
 * Was three: Open, Needs Billing, Billed. Kristine, 25 Aug: "We also need to be
 * able to view active and completed just like the Board." Needs Billing was
 * never a third kind of work, it is a billing state of finished work, so it
 * lives inside Completed as a column filter rather than as its own tab. Sara
 * still reaches her invoicing list in one click from the Status column.
 */
type ViewFilter = 'active' | 'completed';

const VIEW_STATUS: Record<ViewFilter, ServiceItemStatus[]> = {
    active: ['open'],
    completed: SERVICE_COMPLETED_STATUSES,
};

const CREW_NONE = 'No crew';
const SECTION_NONE = 'No section';

// Column ids double as the sort, filter and export keys. One list keeps the
// header, the row, the menu and the CSV from drifting apart.
const COLUMNS = [
    { id: 'requested', label: 'Requested Date', width: '150px' },
    { id: 'community', label: 'Community/Builder', width: '220px' },
    { id: 'lot', label: 'Lot', width: '120px' },
    { id: 'schedule', label: 'Scheduled Date', width: '170px' },
    { id: 'work', label: 'Type of Work', width: 'minmax(260px, 380px)' },
    { id: 'crew', label: 'Crew', width: '110px' },
    { id: 'status', label: 'Status', width: '130px' },
    { id: 'billable', label: 'Billable', width: '110px' },
    { id: 'notes', label: 'Notes', width: 'minmax(150px, 200px)' },
    { id: 'priority', label: 'Priority', width: '90px' },
    { id: 'section', label: 'Section', width: '110px' },
] as const;

/**
 * Grid template, built from the columns actually on screen.
 *
 * Was a module constant over every column. Columns can be hidden per person
 * now, so the template has to follow the same list the header and the row
 * cells follow. One list driving all three is what stops a heading sitting
 * over the wrong data, which is exactly what nearly happened on the column
 * reorder on 31 Aug.
 */
function gridTemplateFor(visibleColIds: readonly string[]): string {
    const widths = COLUMNS.filter((c) => visibleColIds.includes(c.id)).map((c) => c.width);
    return `40px ${widths.join(' ')}`;
}

/**
 * Vertical rule between columns. Kristine, 2 Sep: "Add grid lines between
 * columns just like scheduling board". The schedule board puts border-r on
 * every cell, this board had none at all.
 */
const CELL_BORDER = 'border-r border-graphite-200 dark:border-graphite-900';

/** The text a column shows and searches on. */
function columnText(item: ServiceItemWithContext, colId: string): string {
    switch (colId) {
        case 'community':
            return communityLabel(item);
        case 'lot':
            return item.lot ?? '';
        case 'work':
            return item.type_of_work ?? '';
        case 'crew':
            return crewLabel(item) ?? '';
        case 'schedule':
            return scheduleLabel(item);
        case 'section':
            return item.community?.section?.name ?? '';
        case 'priority':
            return SERVICE_PRIORITY_LABELS[item.priority];
        case 'billable':
            if (!item.billable) return '';
            return item.epo_amount != null ? `$${item.epo_amount.toFixed(2)}` : 'Billable';
        case 'status':
            return SERVICE_STATUS_LABELS[item.status];
        case 'requested':
            return formatDateShort(item.email_date);
        case 'notes':
            return item.notes ?? '';
        default:
            return '';
    }
}

/**
 * The value a column filters by.
 *
 * Differs from columnText on purpose. Filtering by crew should offer "No crew"
 * as a pickable value, and filtering by billable should offer Yes and No, not
 * every distinct dollar amount, which would produce a menu hundreds of rows long
 * that nobody can use.
 */
function columnFacet(item: ServiceItemWithContext, colId: string): string {
    switch (colId) {
        case 'crew':
            return crewLabel(item) ?? CREW_NONE;
        case 'section':
            return item.community?.section?.name ?? SECTION_NONE;
        case 'billable':
            return item.billable ? 'Billable' : 'Not billable';
        case 'schedule':
            return SERVICE_STATE_LABELS[item.schedule_state];
        case 'work':
        case 'notes':
            // Free text. A checkbox list of 2,000 distinct work descriptions is
            // useless, so these filter by the contains box only.
            return '';
        default:
            return columnText(item, colId);
    }
}

/** The value a column sorts by. Dates and money must not sort as text. */
function columnSort(item: ServiceItemWithContext, colId: string): string {
    switch (colId) {
        case 'schedule':
            // Dated items first in date order, then the states in urgency order,
            // so a sorted list reads the way the day is actually planned.
            if (item.scheduled_date) return `1${item.scheduled_date}`;
            return `2${item.schedule_state}`;
        case 'status':
            return item.status;
        case 'requested':
            return item.email_date ?? '9999';
        case 'billable':
            return item.epo_amount != null
                ? `1${String(item.epo_amount).padStart(12, '0')}`
                : item.billable
                  ? '2'
                  : '3';
        case 'lot': {
            // "12" must sort before "100", and "A18" and "ALL LOTS" still need a
            // stable home rather than throwing the comparison.
            const n = Number(String(item.lot ?? '').replace(/[^0-9]/g, ''));
            return Number.isFinite(n) && String(item.lot ?? '').match(/^\d/)
                ? `1${String(n).padStart(8, '0')}`
                : `2${item.lot ?? ''}`;
        }
        default:
            return columnText(item, colId).toLowerCase();
    }
}

/**
 * Sort key for the values listed in a column menu.
 *
 * Requested Date now reads as MM/DD/YY, and MM/DD/YY sorts wrong as plain text
 * the moment the list crosses a year: 01/05/26 would come before 12/01/25.
 * Dates get rewritten to YY MM DD for ordering only. Everything else is
 * returned untouched.
 */
const FACET_DATE = /^(\d{2})\/(\d{2})\/(\d{2})$/;

function facetSortKey(v: string): string {
    const m = FACET_DATE.exec(v);
    return m ? `${m[3]}${m[1]}${m[2]}` : v;
}

function compareValues(a: string, b: string, dir: SortDir): number {
    const r = a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    return dir === 'asc' ? r : -r;
}

function csvCell(v: string | null | undefined): string {
    return `"${String(v ?? '').replace(/\r?\n/g, ' ').replace(/"/g, '""')}"`;
}

function exportCsv(items: ServiceItemWithContext[], view: ViewFilter) {
    const header = [...COLUMNS.map((c) => c.label), 'Concrete', 'Extra time', 'Warranty'];
    const lines = [
        header.map(csvCell).join(','),
        ...items.map((it) =>
            [
                ...COLUMNS.map((c) => columnText(it, c.id)),
                it.requires_concrete ? 'Yes' : '',
                it.extended_time ? 'Yes' : '',
                it.is_warranty ? 'Yes' : '',
            ]
                .map(csvCell)
                .join(','),
        ),
    ].join('\r\n');
    const blob = new Blob(['\uFEFF' + lines], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ppc-service-${view}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * Status pill colours.
 *
 * Open is neutral. Both genuinely finished states are green, which is what
 * Kristine asked for and what the schedule board already does. Needs Billing
 * stays blue on purpose: the work is done but the money is not, and colouring
 * it the same as Billed would hide the one state somebody still has to act on.
 */
function statusTone(status: ServiceItemStatus): string {
    switch (status) {
        case 'open':
            return 'bg-graphite-200 dark:bg-graphite-800 text-graphite-700 dark:text-graphite-300';
        case 'completed_needs_billing':
            return 'bg-signal-500 text-graphite-900';
        default:
            return 'bg-done-500 text-graphite-900';
    }
}

export function ServiceBoardPage() {
    const { role } = useAuth();
    const isAdmin = role === 'admin';

    const [mode, setMode] = useState<'list' | 'route'>('list');
    const [view, setView] = useState<ViewFilter>('active');
    const [search, setSearch] = useState('');
    const [communityFilter, setCommunityFilter] = useState<string>('all');
    const [crewFilter, setCrewFilter] = useState<string>('all');
    const [workFilter, setWorkFilter] = useState<'all' | 'concrete' | 'extended'>('all');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
    const [bulkMsg, setBulkMsg] = useState<string | null>(null);
    const [addOpen, setAddOpen] = useState(false);

    const [sort, setSort] = useState<{ colId: string; dir: SortDir } | null>(null);
    // Kristine, 2 Sep, asked for the Compact button and the column hiding the
    // schedule board has. Compact is a toggle she flips to send and flips back,
    // so it is not persisted. Hidden columns are, per person, under their own
    // key so the two boards do not fight over one list.
    const [compact, setCompact] = useState(false);
    const [columnsPanel, setColumnsPanel] = useState<{ x: number; y: number } | null>(null);
    const { data: prefs } = useUserPreferences();
    const setPrefs = useSetUserPreferences();
    const hiddenColumns = prefs?.service?.hiddenColumns ?? [];
    const visibleColumns = COLUMNS.filter((c) => !hiddenColumns.includes(c.id));
    const visibleColIds = visibleColumns.map((c) => c.id);
    const gridTemplate = gridTemplateFor(visibleColIds);
    const headerPad = compact ? 'px-3 py-0.5' : 'px-3 py-2.5';

    function setHiddenColumns(next: string[]) {
        setPrefs.mutate({ ...prefs, service: { ...prefs?.service, hiddenColumns: next } });
    }
    const [textFilters, setTextFilters] = useState<Record<string, string>>({});
    const [valueFilters, setValueFilters] = useState<Record<string, string[]>>({});
    const [menu, setMenu] = useState<{ colId: string; label: string; x: number; y: number } | null>(
        null,
    );

    const { data: items = [], isLoading, error } = useServiceItems({ status: VIEW_STATUS[view] });
    const { data: counts } = useServiceStatusCounts();
    const { data: communities = [] } = useServiceCommunities();
    const { data: crews = [] } = useServiceCrews('service');
    const { data: sections = [] } = useServiceSections();
    const bulkComplete = useBulkCompleteServiceItems();
    const bulkDelete = useBulkDeleteServiceItems();
    // Delete is irreversible and there is no undo, so the button arms on the
    // first click and only deletes on the second. Any change to the selection
    // disarms it, so a click cannot land on a set of rows the person did not
    // mean to be looking at.
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        setConfirmDelete(false);
    }, [selectedIds]);

    const activeTextFilters = useMemo(
        () => Object.entries(textFilters).filter(([, v]) => v.trim().length > 0),
        [textFilters],
    );
    const activeValueFilters = useMemo(
        () => Object.entries(valueFilters).filter(([, v]) => v.length > 0),
        [valueFilters],
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        let out = items;

        if (q) {
            out = out.filter((it) =>
                [
                    communityLabel(it),
                    it.lot,
                    it.type_of_work,
                    it.notes,
                    it.builder_contact,
                    crewLabel(it),
                    it.address,
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase()
                    .includes(q),
            );
        }
        if (communityFilter !== 'all') {
            out = out.filter((it) => it.community_id === communityFilter);
        }
        if (crewFilter !== 'all') {
            out = out.filter((it) =>
                crewFilter === CREW_NONE ? !it.crew_id : it.crew_id === crewFilter,
            );
        }
        if (workFilter === 'concrete') {
            out = out.filter((it) => it.requires_concrete);
        } else if (workFilter === 'extended') {
            out = out.filter((it) => it.extended_time);
        }
        for (const [colId, needle] of activeTextFilters) {
            const n = needle.trim().toLowerCase();
            out = out.filter((it) => columnText(it, colId).toLowerCase().includes(n));
        }
        for (const [colId, vals] of activeValueFilters) {
            const set = new Set(vals);
            out = out.filter((it) => set.has(columnFacet(it, colId)));
        }
        if (sort) {
            out = [...out].sort((a, b) =>
                compareValues(columnSort(a, sort.colId), columnSort(b, sort.colId), sort.dir),
            );
        }
        return out;
    }, [
        items,
        search,
        communityFilter,
        crewFilter,
        workFilter,
        activeTextFilters,
        activeValueFilters,
        sort,
    ]);

    // Facets come from the rows the other filters already left standing, so a
    // menu never offers a value that would return nothing.
    const menuFacets = useMemo(() => {
        if (!menu) return [];
        const seen = new Set<string>();
        for (const it of filtered) {
            const v = columnFacet(it, menu.colId);
            if (v) seen.add(v);
        }
        const existing = valueFilters[menu.colId] ?? [];
        for (const v of existing) seen.add(v);
        return [...seen].sort((a, b) =>
            facetSortKey(a).localeCompare(facetSortKey(b), undefined, { numeric: true }),
        );
    }, [menu, filtered, valueFilters]);

    const crewOptions = useMemo(
        () => crews.map((c) => ({ value: c.id, label: c.name })),
        [crews],
    );
    const communityOptions = useMemo(
        () => communities.map((c) => ({ value: c.id, label: c.display_name })),
        [communities],
    );

    const selectedItems = useMemo(
        () => filtered.filter((it) => selectedIds.has(it.id)),
        [filtered, selectedIds],
    );

    const shownIds = useMemo(() => filtered.map((it) => it.id), [filtered]);
    const allShownSelected = shownIds.length > 0 && shownIds.every((id) => selectedIds.has(id));
    const someShownSelected = shownIds.some((id) => selectedIds.has(id));

    function toggleAllShown() {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allShownSelected) shownIds.forEach((id) => next.delete(id));
            else shownIds.forEach((id) => next.add(id));
            return next;
        });
    }

    function toggleOne(id: string) {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    function openMenu(e: React.MouseEvent, colId: string, label: string) {
        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setMenu({ colId, label, x: Math.min(r.left, window.innerWidth - 300), y: r.bottom + 4 });
    }

    /**
     * Complete a selection, routed by billable.
     *
     * Billable work goes to Needs Billing so somebody prices and invoices it.
     * Non-billable goes to Completed. That is Sara's blue-versus-green rule, and
     * splitting it here is what stops a billable job being quietly closed out
     * without ever being invoiced.
     *
     * Non-billable used to go to Billed, which was the only finished state that
     * existed and which says an invoice went out. Fixed 25 Aug.
     */
    async function handleBulkDelete() {
        const ids = selectedItems.map((i) => i.id);
        try {
            const removed = await bulkDelete.mutateAsync(ids);
            setBulkMsg(`${removed} ${removed === 1 ? 'item' : 'items'} deleted.`);
        } catch (e) {
            setBulkMsg(e instanceof Error ? e.message : 'Could not delete those items.');
        }
        setConfirmDelete(false);
        setSelectedIds(new Set());
    }

    async function handleBulkComplete() {
        const billableIds = selectedItems.filter((i) => i.billable).map((i) => i.id);
        const nonBillableIds = selectedItems.filter((i) => !i.billable).map((i) => i.id);
        try {
            const res = await bulkComplete.mutateAsync({
                billableIds,
                nonBillableIds,
                isoDate: new Date().toISOString().slice(0, 10),
            });
            const parts: string[] = [];
            if (res.needsBilling) parts.push(`${res.needsBilling} moved to Needs Billing`);
            if (res.completed) parts.push(`${res.completed} marked Completed`);
            setBulkMsg(parts.join(', ') || 'Nothing changed');
            setSelectedIds(new Set());
        } catch (e) {
            setBulkMsg(e instanceof Error ? e.message : 'Could not complete those items');
        }
    }

    function clearAllFilters() {
        setTextFilters({});
        setValueFilters({});
        setSort(null);
        setSearch('');
        setCommunityFilter('all');
        setCrewFilter('all');
        setWorkFilter('all');
    }

    const filtersActive =
        activeTextFilters.length > 0 ||
        activeValueFilters.length > 0 ||
        !!sort ||
        !!search.trim() ||
        communityFilter !== 'all' ||
        crewFilter !== 'all' ||
        workFilter !== 'all';

    if (mode === 'route') {
        return (
            <div className="flex flex-col h-full">
                <div className="px-4 md:px-6 pt-5 pb-3 border-b border-graphite-200 dark:border-graphite-800">
                    <ModeSwitch mode={mode} onChange={setMode} />
                </div>
                <ServiceRouteView crews={crews} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="m-6 border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 px-4 py-3 rounded font-mono text-sm">
                Error loading service items: {(error as Error).message}
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            {/* ---------------- toolbar ---------------- */}
            <div className="px-4 md:px-6 pt-5 pb-3 border-b border-graphite-200 dark:border-graphite-800">
                <div className="mb-4">
                    <ModeSwitch mode={mode} onChange={setMode} />
                </div>

                <div className="flex flex-wrap items-end gap-4">
                    <div>
                        <Label>View</Label>
                        <div className="flex border border-graphite-300 dark:border-graphite-700 rounded overflow-hidden h-9">
                            {(
                                [
                                    ['active', 'Active', counts?.active],
                                    ['completed', 'Completed', counts?.completed],
                                ] as [ViewFilter, string, number | undefined][]
                            ).map(([key, label, count]) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => {
                                        setView(key);
                                        setSelectedIds(new Set());
                                    }}
                                    className={cn(
                                        'px-3 font-mono text-[11px] uppercase tracking-widest transition-colors flex items-center gap-2',
                                        key === 'completed' &&
                                            'border-l border-graphite-300 dark:border-graphite-700',
                                        view === key
                                            ? key === 'completed'
                                                ? 'bg-done-500 text-graphite-900'
                                                : 'bg-signal-500 text-graphite-900'
                                            : 'text-graphite-600 dark:text-graphite-400 hover:bg-graphite-100 dark:hover:bg-graphite-900',
                                    )}
                                >
                                    {label}
                                    {count != null && (
                                        <span className="tabular text-[10px] opacity-80">{count}</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="min-w-[240px] flex-1 max-w-md">
                        <Label htmlFor="svc-search">Search</Label>
                        <Input
                            id="svc-search"
                            placeholder="Community, lot, work, contact, notes..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <div className="min-w-[190px]">
                        <Label htmlFor="svc-community">Community</Label>
                        <Select
                            id="svc-community"
                            value={communityFilter}
                            onChange={(e) => setCommunityFilter(e.target.value)}
                        >
                            <option value="all">All communities</option>
                            {communities.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.display_name}
                                </option>
                            ))}
                        </Select>
                    </div>

                    <div className="min-w-[150px]">
                        <Label htmlFor="svc-crew">Crew</Label>
                        <Select
                            id="svc-crew"
                            value={crewFilter}
                            onChange={(e) => setCrewFilter(e.target.value)}
                        >
                            <option value="all">All crews</option>
                            {crews.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                            <option value={CREW_NONE}>{CREW_NONE}</option>
                        </Select>
                    </div>

                    <div className="min-w-[150px]">
                        <Label htmlFor="svc-work">Work type</Label>
                        <Select
                            id="svc-work"
                            value={workFilter}
                            onChange={(e) =>
                                setWorkFilter(e.target.value as 'all' | 'concrete' | 'extended')
                            }
                        >
                            <option value="all">All work</option>
                            <option value="concrete">Concrete only</option>
                            <option value="extended">Extra time only</option>
                        </Select>
                    </div>

                    <div className="ml-auto flex items-center gap-3 pb-1">
                        <span className="font-mono text-xs text-graphite-500 tabular">
                            {filtered.length} / {items.length}
                        </span>
                        {filtersActive && (
                            <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                                Clear filters
                            </Button>
                        )}
                        <Button
                            variant={hiddenColumns.length ? 'primary' : 'ghost'}
                            size="sm"
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                const r = e.currentTarget.getBoundingClientRect();
                                setColumnsPanel({
                                    x: Math.min(r.left, window.innerWidth - 272),
                                    y: r.bottom + 2,
                                });
                            }}
                            title="Show or hide columns, saved to your account"
                        >
                            Columns{hiddenColumns.length ? ` (${hiddenColumns.length})` : ''}
                        </Button>
                        <Button
                            variant={compact ? 'primary' : 'ghost'}
                            size="sm"
                            onClick={() => setCompact((v) => !v)}
                            title="Squeeze the rows to one line so a filtered list fits in one screen grab"
                        >
                            {compact ? 'Full rows' : 'Compact'}
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => exportCsv(filtered, view)}
                            disabled={filtered.length === 0}
                        >
                            Export
                        </Button>
                        {isAdmin && (
                            <Button variant="primary" size="sm" onClick={() => setAddOpen(true)}>
                                + New item
                            </Button>
                        )}
                    </div>
                </div>

                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-graphite-500">
                    Click any column heading to sort or filter it. Tick rows to act on several at once.
                </p>
            </div>

            {/* ---------------- bulk bar ---------------- */}
            {isAdmin && selectedItems.length > 0 && (
                <div className="px-4 md:px-6 py-2.5 bg-signal-500/10 border-b border-signal-500/30 flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-xs text-graphite-900 dark:text-graphite-100 tabular">
                        {selectedItems.length} selected
                    </span>
                    <span className="font-mono text-[10px] text-graphite-500 uppercase tracking-widest">
                        {selectedItems.filter((i) => i.billable).length} billable
                    </span>
                    <div className="flex-1" />
                    {view === 'active' && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleBulkComplete}
                            disabled={bulkComplete.isPending}
                        >
                            {bulkComplete.isPending ? 'Saving...' : 'Mark complete'}
                        </Button>
                    )}
                    {confirmDelete ? (
                        <>
                            <span className="font-mono text-xs text-red-600 dark:text-red-400">
                                Delete {selectedItems.length} permanently? This cannot be undone.
                            </span>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleBulkDelete}
                                disabled={bulkDelete.isPending}
                            >
                                {bulkDelete.isPending ? 'Deleting...' : 'Yes, delete'}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
                                Cancel
                            </Button>
                        </>
                    ) : (
                        <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(true)}>
                            Delete
                        </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
                        Clear selection
                    </Button>
                </div>
            )}

            {bulkMsg && (
                <button
                    type="button"
                    onClick={() => setBulkMsg(null)}
                    className="px-4 md:px-6 py-2 text-left bg-done-500/10 border-b border-done-500/30 font-mono text-xs text-graphite-800 dark:text-graphite-200"
                >
                    {bulkMsg}
                    <span className="ml-2 text-graphite-500">dismiss</span>
                </button>
            )}

            {/* ---------------- table ---------------- */}
            <div className="flex-1 min-h-0 overflow-auto">
                <div className="min-w-max">
                    <div
                        className="grid sticky top-0 z-20 bg-graphite-50 dark:bg-graphite-950 border-b border-graphite-200 dark:border-graphite-800"
                        style={{ gridTemplateColumns: gridTemplate }}
                    >
                        <div className={cn(headerPad, CELL_BORDER, 'flex items-center')}>
                            {isAdmin && (
                                <input
                                    type="checkbox"
                                    aria-label="Select all shown"
                                    checked={allShownSelected}
                                    ref={(el) => {
                                        if (el) el.indeterminate = someShownSelected && !allShownSelected;
                                    }}
                                    onChange={toggleAllShown}
                                    className="accent-signal-600"
                                />
                            )}
                        </div>
                        {visibleColumns.map((c) => {
                            const isFiltered =
                                (textFilters[c.id]?.trim().length ?? 0) > 0 ||
                                (valueFilters[c.id]?.length ?? 0) > 0;
                            const isSorted = sort?.colId === c.id;
                            return (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={(e) => openMenu(e, c.id, c.label)}
                                    className={cn(
                                        headerPad,
                                        CELL_BORDER,
                                        'text-left font-mono text-[10px] uppercase tracking-[0.15em] transition-colors',
                                        'hover:bg-graphite-100 dark:hover:bg-graphite-900',
                                        isFiltered || isSorted
                                            ? 'text-signal-700 dark:text-signal-400'
                                            : 'text-graphite-600 dark:text-graphite-400',
                                    )}
                                >
                                    {c.label}
                                    {isSorted && (sort!.dir === 'asc' ? ' \u2191' : ' \u2193')}
                                    {isFiltered && ' \u2022'}
                                </button>
                            );
                        })}
                    </div>

                    {isLoading ? (
                        <div className="px-6 py-10 font-mono text-xs text-graphite-500 uppercase tracking-widest">
                            Loading service items...
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="px-6 py-10">
                            <EmptyState
                                title={
                                    filtersActive ? 'Nothing matches those filters' : 'No items in this view'
                                }
                                description={
                                    filtersActive
                                        ? 'Clear the filters to see the full list.'
                                        : view === 'completed'
                                          ? 'Items you mark complete land here and stay. Nothing is deleted.'
                                          : 'Use New item to add one, or mark completed work back to open to bring it here.'
                                }
                            />
                        </div>
                    ) : (
                        filtered.map((item) => (
                            <ServiceRow
                                key={item.id}
                                item={item}
                                selectable={isAdmin}
                                editable={isAdmin}
                                crewOptions={crewOptions}
                                communityOptions={communityOptions}
                                selected={selectedIds.has(item.id)}
                                onToggle={() => toggleOne(item.id)}
                                compact={compact}
                                visibleColIds={visibleColIds}
                            />
                        ))
                    )}
                </div>
            </div>

            {columnsPanel &&
                createPortal(
                    <ColumnsPanel
                        x={columnsPanel.x}
                        y={columnsPanel.y}
                        columns={COLUMNS.map((c) => ({ id: c.id, label: c.label }))}
                        hidden={hiddenColumns}
                        lockedIds={['community', 'lot']}
                        onChange={setHiddenColumns}
                        onClose={() => setColumnsPanel(null)}
                    />,
                    document.body
                )}

            {menu &&
                createPortal(
                    <ColumnMenu
                        label={menu.label}
                        x={menu.x}
                        y={menu.y}
                        facets={menuFacets}
                        sortDir={sort?.colId === menu.colId ? sort.dir : null}
                        text={textFilters[menu.colId] ?? ''}
                        selected={valueFilters[menu.colId] ?? []}
                        onSort={(dir) => setSort(dir ? { colId: menu.colId, dir } : null)}
                        onText={(v) => setTextFilters((p) => ({ ...p, [menu.colId]: v }))}
                        onSelected={(vals) => setValueFilters((p) => ({ ...p, [menu.colId]: vals }))}
                        onClose={() => setMenu(null)}
                    />,
                    document.body,
                )}

            <AddServiceItemDialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                communityOptions={communityOptions}
                crewOptions={crewOptions}
            />

            {sections.length > 0 && (
                <div className="px-4 md:px-6 py-2 border-t border-graphite-200 dark:border-graphite-800 flex flex-wrap gap-x-5 gap-y-1">
                    {sections.map((s) => {
                        const n = filtered.filter((i) => i.community?.section?.id === s.id).length;
                        return (
                            <span key={s.id} className="font-mono text-[10px] uppercase tracking-widest text-graphite-500">
                                {s.name} <span className="text-graphite-800 dark:text-graphite-200 tabular">{n}</span>
                            </span>
                        );
                    })}
                    {(() => {
                        const n = filtered.filter((i) => !i.community?.section).length;
                        return n > 0 ? (
                            <span className="font-mono text-[10px] uppercase tracking-widest text-amber-600 dark:text-amber-400">
                                No section <span className="tabular">{n}</span>
                            </span>
                        ) : null;
                    })()}
                </div>
            )}
        </div>
    );
}

function ModeSwitch({
    mode,
    onChange,
}: {
    mode: 'list' | 'route';
    onChange: (m: 'list' | 'route') => void;
}) {
    return (
        <div className="inline-flex border border-graphite-300 dark:border-graphite-700 rounded overflow-hidden">
            {(
                [
                    ['list', 'Service list'],
                    ['route', 'Daily route'],
                ] as ['list' | 'route', string][]
            ).map(([key, label]) => (
                <button
                    key={key}
                    type="button"
                    onClick={() => onChange(key)}
                    className={cn(
                        'px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest transition-colors',
                        mode === key
                            ? 'bg-signal-500 text-graphite-900'
                            : 'text-graphite-600 dark:text-graphite-400 hover:bg-graphite-100 dark:hover:bg-graphite-900',
                    )}
                >
                    {label}
                </button>
            ))}
        </div>
    );
}

function ServiceRow({
    item,
    selectable,
    editable,
    crewOptions,
    communityOptions,
    selected,
    onToggle,
    compact,
    visibleColIds,
}: {
    item: ServiceItemWithContext;
    selectable: boolean;
    editable: boolean;
    crewOptions: { value: string; label: string }[];
    communityOptions: { value: string; label: string }[];
    selected: boolean;
    onToggle: () => void;
    /** One line per row, for sending a filtered list as a screen grab. */
    compact: boolean;
    /** Column ids on screen, in order. Same list the header renders from. */
    visibleColIds: string[];
}) {
    const pad = compact ? 'px-3 py-0.5' : 'px-3 py-2.5';
    const shown = new Set(visibleColIds);
    const unassigned = !item.community_id;
    const ro = !editable;
    const done = item.status !== 'open';

    const stateOptions = SERVICE_STATE_ORDER.map((v) => ({
        value: v,
        label: SERVICE_STATE_LABELS[v],
    }));
    // Values are stringified because a <select> only carries strings. The parse
    // prop on EditableSelect turns the choice back into a number on the way to
    // the database, which is now a smallint.
    const priorityOptions = SERVICE_PRIORITY_ORDER.map((v) => ({
        value: String(v),
        label: SERVICE_PRIORITY_LABELS[v],
    }));
    // Completion is a status change, so it runs through reconcileCompletion and
    // stamps a completion date. Not clearable: the column is NOT NULL.
    const statusOptions = SERVICE_STATUS_ORDER.map((v) => ({
        value: v,
        label: SERVICE_STATUS_LABELS[v],
    }));

    const scheduleTone =
        item.schedule_state === 'asap'
            ? 'bg-amber-500 text-graphite-900'
            : item.schedule_state === 'hold'
              ? 'bg-graphite-400 text-graphite-900'
              : item.schedule_state === 'scheduled'
                ? 'bg-done-500 text-graphite-900'
                : item.schedule_state === 'ready'
                  ? 'bg-signal-500 text-graphite-900'
                  : 'bg-graphite-200 dark:bg-graphite-800 text-graphite-700 dark:text-graphite-300';

    return (
        <div
            className={cn(
                'grid border-b border-graphite-200 dark:border-graphite-900 transition-colors',
                selected
                    ? 'bg-signal-500/[0.08]'
                    : done
                      ? // Finished rows carry a green wash, the way a completed
                        // row reads on the schedule board. Kept faint so a
                        // screen of them still scans as rows rather than paint.
                        'bg-done-500/[0.06] hover:bg-done-500/[0.10]'
                      : 'hover:bg-graphite-100/60 dark:hover:bg-graphite-900/60',
            )}
            style={{ gridTemplateColumns: gridTemplateFor(visibleColIds) }}
        >
            <div className={cn(pad, CELL_BORDER, 'flex items-start')}>
                {selectable && (
                    <input
                        type="checkbox"
                        aria-label={`Select ${item.type_of_work}`}
                        checked={selected}
                        onChange={onToggle}
                        className="mt-0.5 accent-signal-600"
                    />
                )}
            </div>

            {shown.has('requested') ? (
            <div className={cn(pad, CELL_BORDER, 'min-w-0')}>
                <EditableDate
                    item={item}
                    field="email_date"
                    value={item.email_date}
                    className="text-[11px] text-graphite-600 dark:text-graphite-400"
                    disabled={ro}
                />
            </div>
            ) : null}

            {shown.has('community') ? (
            <div className={cn(pad, CELL_BORDER, 'min-w-0')}>
                <EditableSelect
                    item={item}
                    field="community_id"
                    value={item.community_id}
                    options={communityOptions}
                    placeholder={item.community_raw ? `${item.community_raw} (unassigned)` : '(no community)'}
                    disabled={ro}
                    render={(label) => (
                        <span
                            className={cn(
                                'text-sm block truncate',
                                unassigned
                                    ? 'text-amber-600 dark:text-amber-400'
                                    : 'text-graphite-900 dark:text-graphite-100',
                            )}
                        >
                            {label ?? communityLabel(item)}
                        </span>
                    )}
                />
                <EditableText
                    item={item}
                    field="builder_contact"
                    value={item.builder_contact}
                    placeholder="add contact"
                    disabled={ro}
                    className={cn(
                        'font-mono text-[10px] uppercase tracking-wider',
                        item.is_warranty
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-graphite-500',
                    )}
                />
            </div>
            ) : null}

            {shown.has('lot') ? (
            <div className={cn(pad, CELL_BORDER, 'min-w-0')}>
                <EditableText
                    item={item}
                    field="lot"
                    value={item.lot}
                    mono
                    disabled={ro}
                    className="font-mono text-sm tabular text-graphite-800 dark:text-graphite-200"
                />
            </div>
            ) : null}

            {shown.has('schedule') ? (
            <div className={cn(pad, CELL_BORDER, 'min-w-0')}>
                <EditableSchedule
                    item={item}
                    stateOptions={stateOptions}
                    tone={scheduleTone}
                    disabled={ro}
                />
            </div>
            ) : null}

            {shown.has('work') ? (
            <div className={cn(pad, CELL_BORDER, 'min-w-0')}>
                <EditableTextarea
                    item={item}
                    field="type_of_work"
                    value={item.type_of_work}
                    disabled={ro}
                    required
                    className="text-sm text-graphite-800 dark:text-graphite-200"
                />
                <div className="mt-1 flex flex-wrap gap-1">
                    <EditableFlag
                        item={item}
                        field="requires_concrete"
                        value={item.requires_concrete}
                        label="Concrete"
                        onClass="bg-concrete-500 text-white"
                        disabled={ro}
                    />
                    <EditableFlag
                        item={item}
                        field="extended_time"
                        value={item.extended_time}
                        label="Extra time"
                        onClass="bg-signal-500 text-graphite-900"
                        disabled={ro}
                    />
                </div>
            </div>
            ) : null}

            {shown.has('crew') ? (
            <div className={cn(pad, CELL_BORDER, 'min-w-0')}>
                <EditableSelect
                    item={item}
                    field="crew_id"
                    value={item.crew_id}
                    options={crewOptions}
                    placeholder={item.crew_raw ? `${item.crew_raw} (unmatched)` : CREW_NONE}
                    disabled={ro}
                    render={(label) => (
                        <span className="text-sm text-graphite-700 dark:text-graphite-300">
                            {label || (
                                <span className="text-graphite-500">
                                    {item.crew_raw ? `${item.crew_raw} (unmatched)` : CREW_NONE}
                                </span>
                            )}
                        </span>
                    )}
                />
            </div>
            ) : null}

            {shown.has('status') ? (
            <div className={cn(pad, CELL_BORDER, 'min-w-0')}>
                <EditableSelect
                    item={item}
                    field="status"
                    value={item.status}
                    options={statusOptions}
                    allowClear={false}
                    disabled={ro}
                    render={(label) => (
                        <span
                            className={cn(
                                'font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded',
                                statusTone(item.status),
                            )}
                        >
                            {label}
                        </span>
                    )}
                />
            </div>
            ) : null}

            {shown.has('billable') ? (
            <div className={cn(pad, CELL_BORDER, 'min-w-0')}>
                <EditableMoney
                    item={item}
                    value={item.epo_amount}
                    billable={item.billable}
                    disabled={ro}
                    className="text-graphite-800 dark:text-graphite-200"
                />
            </div>
            ) : null}

            {shown.has('notes') ? (
            <div className={cn(pad, CELL_BORDER, 'min-w-0')}>
                <EditableTextarea
                    item={item}
                    field="notes"
                    value={item.notes}
                    placeholder="add note"
                    small
                    disabled={ro}
                    className="text-[11px] leading-snug text-graphite-600 dark:text-graphite-400"
                />
            </div>
            ) : null}

            {shown.has('priority') ? (
            <div className={cn(pad, CELL_BORDER, 'min-w-0')}>
                <EditableSelect
                    item={item}
                    field="priority"
                    value={String(item.priority)}
                    options={priorityOptions}
                    placeholder={String(SERVICE_PRIORITY_DEFAULT)}
                    allowClear={false}
                    parse={(v) => Number(v)}
                    disabled={ro}
                    render={(label) =>
                        // 3 is the default and means nobody has ranked this, so
                        // it reads as plain text the way Normal used to. Amber
                        // for the top two, grey for the bottom two. Blue is not
                        // available here: Kristine uses it for extra time.
                        item.priority === SERVICE_PRIORITY_DEFAULT ? (
                            <span className="text-graphite-400 text-[11px]">{label}</span>
                        ) : (
                            <span
                                className={cn(
                                    'font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded',
                                    item.priority === 1
                                        ? 'bg-amber-500 text-graphite-900'
                                        : item.priority === 2
                                          ? 'bg-amber-500/70 text-graphite-900'
                                          : 'bg-graphite-300 text-graphite-800',
                                )}
                            >
                                {label}
                            </span>
                        )
                    }
                />
            </div>
            ) : null}

            {shown.has('section') ? (
            <div className={cn(pad, CELL_BORDER, 'font-mono text-[11px] text-graphite-600 dark:text-graphite-400 truncate min-w-0')}>
                {item.community?.section?.name ?? (
                    <span className="text-amber-600 dark:text-amber-400">{SECTION_NONE}</span>
                )}
            </div>
            ) : null}
        </div>
    );
}
